/**
 * Subscribes to Supabase Realtime changes on the notifications table for the current user.
 * Invalidates React Query caches when rows are inserted or updated.
 *
 * Ref-counted per userId so multiple hooks (e.g. header badge + profile modal) share one channel.
 * Supabase rejects `.on('postgres_changes')` after `subscribe()` on the same channel topic.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { invalidateNotificationQueries } from "@/utils/queryCache";

type ChannelEntry = {
  channel: RealtimeChannel;
  refCount: number;
};

const channelRegistry = new Map<string, ChannelEntry>();

/** Clears ref-counted channel registry between unit tests. */
export function resetNotificationChannelRegistryForTests(): void {
  channelRegistry.clear();
}

/**
 * Acquire Notification Channel.
 */
function acquireNotificationChannel(
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>
) {
  const channelName = `notifications_realtime_${userId}`;
  const existing = channelRegistry.get(channelName);

  if (existing) {
    existing.refCount += 1;
    return channelName;
  }

  const channel = supabase
    .channel(channelName)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      () => {
        invalidateNotificationQueries(queryClient, userId);
      }
    )
    .subscribe();

  channelRegistry.set(channelName, { channel, refCount: 1 });
  return channelName;
}

/**
 * Release Notification Channel.
 */
function releaseNotificationChannel(channelName: string): void {
  const entry = channelRegistry.get(channelName);
  if (!entry) {
    return;
  }

  entry.refCount -= 1;
  if (entry.refCount > 0) {
    return;
  }

  supabase.removeChannel(entry.channel);
  channelRegistry.delete(channelName);
}

/**
 * Use Notification Realtime.
 */
export function useNotificationRealtime(userId: string | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) {
      return;
    }

    const channelName = acquireNotificationChannel(userId, queryClient);

    return () => {
      releaseNotificationChannel(channelName);
    };
  }, [queryClient, userId]);
}
